import { BehaviorSubject, Subject } from 'common/barrels/rx';
import { IResourceProvider } from 'common/resourceprovider';
import { div } from 'common/barrels/dom';
import { toDefaultShortDateTimeFormat } from 'common/functions/date';
import { undefinedNullOrEmpty } from 'common/functions/string';
import { ColumnInfo, constructTable, makeSortable } from 'common/rxui/rxtable';

import { IDialog, IDialogOptions } from 'runtime/dialogs/dialog';
import { NotificationInfo, SentNotificationInfo, UnsentNotificationInfo } from 'runtime/data/notificationinfo';

import 'runtime/styles/dialogs/notificationsummary.less';

export class NotificationSummaryDialog implements IDialog {
    options: IDialogOptions;
    private dialogContent: JQuery;

    private sentText: string;
    private unsentText: string;

    constructor(resources: IResourceProvider, items: NotificationInfo[], itemTitle: string) {
        this.sentText = resources.getCoreString('NotificationSummarySent');
        this.unsentText = resources.getCoreString('NotificationSummaryUnsent');

        const resized = new Subject<{}>();

        const completeTitle = undefinedNullOrEmpty(itemTitle)
            ? resources.getCoreString('NotificationSummaryTitle')
            : resources.getCoreString('NotificationSummaryTitleWithSeparator') + itemTitle;

        this.options = {
            title: completeTitle,
            width: 1100,
            height: 600,
            dialogClass: 'wf-container wf-worktray-item-dialog',
            draggable: true,
            resizable: true,
            buttons: [
                { text: resources.getCoreString('XamlClose'), click: () => this.close() }
            ],
            resize: (e, ui) => resized.next({})
            
        };
        this.dialogContent = this.buildContent(resources, items, resized);
    }

    content(): JQuery {
        return this.dialogContent;
    }

    close(): void {
        this.dialogContent.dialog('close');
    }

    private buildContent(resources: IResourceProvider, items: NotificationInfo[], resized: Subject<{}>): JQuery {
        return div('form-group dialog-notificationsummary').append(this.summaryTable(resources, items, resized));
    }

    private summaryTable(resources: IResourceProvider, initialItems: NotificationInfo[], resized: Subject<{}>) {
        const items = new BehaviorSubject(initialItems.map(x => this.rowItem(x)));

        const initialColumns = [
            new ColumnInfo('status', resources.getCoreString('NotificationSummaryStatus'), 50, 'pixel'),
            new ColumnInfo('date', resources.getCoreString('NotificationSummaryDate'), 130, 'pixel'),
            new ColumnInfo('step', resources.getCoreString('NotificationSummaryStep'), 100, 'pixel'),
            new ColumnInfo('recipients', resources.getCoreString('NotificationSummaryRecipients'), 200, 'auto'),
            new ColumnInfo('subject', resources.getCoreString('NotificationSummarySubject'), 200, 'auto'),
            new ColumnInfo('messageType', resources.getCoreString('NotificationSummaryMessageType'), 100, 'auto'),
            new ColumnInfo('trackingId', resources.getCoreString('NotificationSummaryTrackingId'), 100, 'auto'),
            new ColumnInfo('attempts', resources.getCoreString('NotificationSummaryAttempts'), 50, 'auto'),
            new ColumnInfo('nextAttempt', resources.getCoreString('NotificationSummaryNextAttempt'), 130, 'pixel')
        ];

        const columns = new BehaviorSubject(initialColumns);

        const { $tableContainer, $table } = constructTable(
            columns,
            items,
            x => x,
            resized,
            20,
            10
        );

        makeSortable($table).subscribe(value => {
            const currentItems = items.getValue();

            if (currentItems.length < 1)
                return;

            const currentColumns = columns.getValue();
            const columnName = currentColumns[value.index].name;

            const coerceToEmpty = (x: string) => undefinedNullOrEmpty(x) ? '' : x;

            const coerceToNegOne = (x: any) => x === null ? -1 : x === undefined ? -1 : x;

            const isNumberColumn = columnName === 'attempts';

            const stringComparer = value.ascending
                ? (a, b) => coerceToEmpty(a[columnName]).localeCompare(coerceToEmpty(b[columnName]))
                : (b, a) => coerceToEmpty(a[columnName]).localeCompare(coerceToEmpty(b[columnName]))

            const numberComparer = value.ascending
                ? (a, b) => coerceToNegOne(a[columnName]) - coerceToNegOne(b[columnName])
                : (b, a) => coerceToNegOne(a[columnName]) - coerceToNegOne(b[columnName]);

            const newlySortedItems = currentItems.slice().sort(isNumberColumn ? numberComparer : stringComparer);
            items.next(newlySortedItems);
        });

        return $tableContainer;
    }

    private rowItem(item: NotificationInfo) {
        if (item instanceof UnsentNotificationInfo) {
            return {
                status: this.unsentText,
                date: toDefaultShortDateTimeFormat(item.queueDate),
                step: item.step,
                recipients: item.recipients,
                subject: item.subject,
                messageType: item.emailMessageType,
                trackingId: item.trackingId,
                attempts: item.attempts,
                nextAttempt: toDefaultShortDateTimeFormat(item.nextAttempt)
            };
        }

        if (item instanceof SentNotificationInfo) {
            return {
                status: this.sentText,
                date: toDefaultShortDateTimeFormat(item.dateSent),
                step: item.step,
                recipients: item.recipients,
                subject: item.subject,
                messageType: item.emailMessageType,
                trackingId: item.trackingId,
                attempts: null,
                nextAttempt: null
            };
        }

        throw new Error('Unknown NotificationInfo subtype!');
    }
}